import React, {useEffect, useRef, useState} from "react";
import {useRouter} from "next/router";
import {withTranslation} from "../../../i18n";
import Modal from "@/shared/modal/Modal";
import CustomForm from "@/shared/form/custom-form/CustomForm";
import Button from "@/shared/button/simpleButton/Button";
import Input from "@/shared/form/input/Input";
import Select from "@/shared/form/select/Select";
import SearchSelect from "@/shared/form/search-select/SearchSelect";
import AutoCompleteAddress from "@/shared/form/autocomplete-adress/AutocompleteAdress";
import PhoneNumberInput from "@/shared/form/phonenumber-input";
import StrongAuthentificationModal from "@/shared/modals/strong-authentification-modal/StrongAuthentificationModal";
import {COUNTRY} from "@/models/viewModel/Country";
import {ECONOMIC_ACTIVITIES, REVENUE_RANGES, ASSET_RANGES} from "@/models/User";
import {STRONG_AUTH_STATUS} from "@/models/StrongAuth";
import UserService from "@/services/domain/UserService";
import AuthService from "@/services/domain/AuthService";
import {useToasts} from "react-toast-notifications";
import css from "./PeriodicReviewModal.module.scss";

const namespacesRequired = ["periodic-review", "common", "economic-activities"];

export const PERIODIC_REVIEW_COMPLETED_EVENT = "periodicReviewCompleted";

// InProgress and Overdue both let the user complete the review in-app via the form.
// Overdue first shows an acknowledgement notice (deadline passed, account debit-blocked);
// clicking "J'ai compris" — or trying to dismiss it — reveals the same form, which is then
// non-dismissible until submitted. Timedout has expired (account closed): a read-only,
// dismissible notice with no form.
const PERIODIC_REVIEW_STATUS = {
  IN_PROGRESS: "InProgress",
  OVERDUE: "Overdue",
  TIMEDOUT: "TimedOut",
};

const isStatus = (value, expected) =>
  typeof value === "string" && value.toLowerCase() === expected.toLowerCase();

const PeriodicReviewModal = ({t}) => {
  const [review, setReview] = useState(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [strongAuthReference, setStrongAuthReference] = useState(null);
  // Overdue only: flips to true once the user acknowledges (or tries to dismiss) the notice,
  // switching the modal from the notice to the data-update form.
  const [acknowledged, setAcknowledged] = useState(false);

  const {addToast} = useToasts();
  const router = useRouter();
  const checked = useRef(false);

  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      checked.current = false;
      setReview(null);
      setVisible(false);
      setAcknowledged(false);
      return;
    }
    if (checked.current) {
      return;
    }
    checked.current = true;
    UserService.getPeriodicReview()
      .then((result) => {
        // must_review covers InProgress + Overdue; also surface the Timedout notice.
        if (result && (result.must_review || isStatus(result.status, PERIODIC_REVIEW_STATUS.TIMEDOUT))) {
          setReview(result);
          setVisible(true);
          if (result.pending_strong_auth_reference) {
            setStrongAuthReference(result.pending_strong_auth_reference);
            setAuthenticating(true);
          }
        }
      })
      .catch(() => {
        checked.current = false;
      });
  }, [router.asPath]);

  const closeModal = () => setVisible(false);

  // Dismissal handler for the modal (overlay / Esc / close button) and the notice CTA.
  // For an unacknowledged Overdue notice, "quitting" doesn't close — it reveals the form.
  // Exception: when the Xpollens relation is inactive the form's submit would 409 (NEEDS_KYC),
  // so we keep the dismissible "contact support" notice instead of trapping the user in a
  // non-dismissible form they can never complete.
  const onHide = () => {
    if (
      review &&
      isStatus(review.status, PERIODIC_REVIEW_STATUS.OVERDUE) &&
      !review.is_relation_inactive &&
      !acknowledged
    ) {
      setAcknowledged(true);
      return;
    }
    closeModal();
  };

  const onSuccess = () => {
    window.dispatchEvent(new Event(PERIODIC_REVIEW_COMPLETED_EVENT));
    closeModal();
    addToast(t("periodic-review:verification-success"), {
      appearance: "success", autoDismiss: true
    });
  };

  const onSubmit = (values) => {
    setLoading(true);
    // Email is intentionally excluded: it has a dedicated confirm-link flow and
    // must not be routed through SCA (backend patches body.email when present).
    const payload = {
      phone: values.phone,
      address_street: values.address_street,
      address_additional: values.address_additional || undefined,
      address_zip_code: values.address_zip_code,
      address_city: values.address_city,
      address_country: values.address_country,
      // Omit when empty: backend filterInput treats "" as a real value and would patch it.
      economic_activity: values.economic_activity || undefined,
      revenue_range: values.revenue_range,
      asset_range: values.asset_range,
    };

    UserService.submitPeriodicReview(payload)
      .then((result) => {
        if (!result.is_strong_auth_resp) {
          onSuccess();
        } else if (result.status !== STRONG_AUTH_STATUS.PENDING) {
          addToast(t("common:errors.E_STRONG_AUTHENTICATION_FAILED"), {
            appearance: "error", autoDismiss: true
          });
        } else {
          setStrongAuthReference(result.reference);
          setAuthenticating(true);
        }
      })
      .catch(() => addToast(t("common:errors.DEFAULT_ERROR_MESSAGE"), {
        appearance: "error", autoDismiss: true
      }))
      .finally(() => setLoading(false));
  };

  const validate = (values) => {
    const errors = {};
    const revenueValues = Object.values(REVENUE_RANGES).map(String);
    const assetValues = Object.values(ASSET_RANGES).map(String);

    if (!values.revenue_range) {
      errors.revenue_range = "E_REQUIRED_REVENUE_RANGE";
    } else if (!revenueValues.includes(String(values.revenue_range))) {
      errors.revenue_range = "E_UNKNOWN_VALUE_REVENUE_RANGE";
    }

    if (!values.asset_range) {
      errors.asset_range = "E_REQUIRED_ASSET_RANGE";
    } else if (!assetValues.includes(String(values.asset_range))) {
      errors.asset_range = "E_UNKNOWN_VALUE_ASSET_RANGE";
    }

    return errors;
  };

  const getInitialValues = (current) => ({
    phone: current.phone || "",
    email: current.email || "",
    address_street: current.address_street || "",
    address_additional: current.address_additional || "",
    address_zip_code: current.address_zip_code || "",
    address_city: current.address_city || "",
    address_country: Object.values(COUNTRY).some((c) => c.code === current.address_country)
      ? current.address_country
      : COUNTRY[0].code,
    economic_activity: current.economic_activity || "",
    revenue_range: current.revenue_range || "",
    asset_range: current.asset_range || "",
  });

  const formatDeadline = (deadline) => {
    if (!deadline) return "";
    try {
      return new Date(deadline).toLocaleDateString("fr-FR");
    } catch (e) {
      return "";
    }
  };

  const renderForm = ({setFieldValue, handleSubmit, handleChange, handleBlur, values, errors, touched}) => (
    <form onSubmit={handleSubmit} noValidate>
      <StrongAuthentificationModal
        visible={authenticating}
        onHide={(outcome) => {
          setAuthenticating(false);
          setStrongAuthReference(null);
          if (outcome === STRONG_AUTH_STATUS.SUCCEEDED) {
            UserService.getPeriodicReview()
              .then((result) => {
                if (result && result.pending_strong_auth_reference) {
                  setReview(result);
                  setStrongAuthReference(result.pending_strong_auth_reference);
                  setAuthenticating(true);
                } else {
                  onSuccess();
                }
              })
              .catch(() => onSuccess());
          } else if (outcome === STRONG_AUTH_STATUS.FAILED) {
            closeModal();
            addToast(t("common:errors.E_STRONG_AUTHENTICATION_FAILED"), {
              appearance: "error", autoDismiss: true
            });
          }
        }}
        strongAuthReference={strongAuthReference}
      />

      <p className={css.intro}>{t("periodic-review:intro")}</p>
      {review.deadline && (
        <p className={css.deadline}>
          {t("periodic-review:deadline", {date: formatDeadline(review.deadline)})}
        </p>
      )}

      <h4 className={css.section}>{t("periodic-review:sections.contact")}</h4>
      <PhoneNumberInput
        inputId="phone"
        inputLabel={t("periodic-review:fields.phone")}
        inputName="phone"
        inputType="phone"
        inputPlaceholder={t("periodic-review:fields.phone")}
        errors={errors}
        value={values.phone}
        onChange={handleChange}
        onBlur={handleBlur}
        setFieldValue={setFieldValue}
        touched={touched}
        required
      />
      <h4 className={css.section}>{t("periodic-review:sections.address")}</h4>
      <div className={css.address__fields}>
        <AutoCompleteAddress
          inputId="address_street"
          inputLabel={t("periodic-review:fields.street")}
          inputName="address_street"
          inputPlaceholder={t("periodic-review:fields.street")}
          errors={errors}
          touched={touched}
          value={values.address_street || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          isPreFilled
          onSelect={(selectedAddress) => {
            handleChange({target: {name: "address_street", value: selectedAddress.value || ""}});
            handleChange({target: {name: "address_additional", value: ""}});
            handleChange({target: {name: "address_city", value: selectedAddress.city || ""}});
            handleChange({target: {name: "address_zip_code", value: selectedAddress.postalCode || ""}});
          }}
        />
        <Input
          inputId="address_additional"
          inputLabel={t("periodic-review:fields.additional")}
          inputName="address_additional"
          inputType="address_additional"
          inputPlaceholder={t("periodic-review:fields.additional")}
          errors={errors}
          value={values.address_additional}
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("address_additional", e.target.value.trim());
          }}
        />
        <Input
          inputId="address_zip_code"
          inputLabel={t("periodic-review:fields.zip_code")}
          inputName="address_zip_code"
          inputType="address_zip_code"
          inputPlaceholder={t("periodic-review:fields.zip_code")}
          errors={errors}
          value={values.address_zip_code}
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("address_zip_code", e.target.value.trim());
          }}
        />
        <Input
          inputId="address_city"
          inputLabel={t("periodic-review:fields.city")}
          inputName="address_city"
          inputType="address_city"
          inputPlaceholder={t("periodic-review:fields.city")}
          errors={errors}
          value={values.address_city}
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("address_city", e.target.value.trim());
          }}
        />
        <SearchSelect
          options={Object.values(COUNTRY).map((val) => ({value: val.code, label: val.name}))}
          defaultValue={COUNTRY[COUNTRY.findIndex((opt) => opt.code === "FR")]}
          label={t("periodic-review:fields.country")}
          inputId="address_country"
          inputName="address_country"
          errors={errors}
          value={values.address_country}
          onBlur={handleBlur}
        />
      </div>

      <h4 className={css.section}>{t("periodic-review:sections.financial")}</h4>
      <Select
        options={Object.values(ECONOMIC_ACTIVITIES).map((val) => ({
          value: val,
          label: t(`economic-activities:economic-activity-${val}`),
        }))}
        label={t("periodic-review:fields.economic_activity")}
        inputId="economic_activity"
        inputName="economic_activity"
        errors={errors}
        value={values.economic_activity}
        onBlur={handleBlur}
        required
      />
      <Select
        options={Object.values(REVENUE_RANGES).map((val) => ({
          value: val,
          label: t(`periodic-review:revenue-ranges.${val}`),
        }))}
        label={t("periodic-review:fields.revenue_range")}
        inputId="revenue_range"
        inputName="revenue_range"
        errors={errors}
        value={values.revenue_range}
        onBlur={handleBlur}
        required
      />
      <Select
        options={Object.values(ASSET_RANGES).map((val) => ({
          value: val,
          label: t(`periodic-review:asset-ranges.${val}`),
        }))}
        label={t("periodic-review:fields.asset_range")}
        inputId="asset_range"
        inputName="asset_range"
        errors={errors}
        value={values.asset_range}
        onBlur={handleBlur}
        required
      />

      <div className={css.btn_container}>
        <Button
          type="submit"
          loading={loading}
          className={css.btn__primary}
          disabled={Object.keys(errors).length > 0}
        >
          {t("periodic-review:submit")}
        </Button>
      </div>
    </form>
  );

  // Overdue (before acknowledgement) and Timedout render this notice. For Overdue its CTA /
  // dismissal advances to the form (see onHide); for Timedout there is nothing actionable
  // in-app, so the CTA simply closes the modal.
  const renderNotice = (status) => {
    const key = isStatus(status, PERIODIC_REVIEW_STATUS.TIMEDOUT) ? "timedout" : "overdue";
    return (
      <div>
        <p className={css.intro}>{t(`periodic-review:notice.${key}`)}</p>
        {isStatus(status, PERIODIC_REVIEW_STATUS.OVERDUE) && (
          <>
            {review.is_relation_inactive && (
              <p className={css.intro}>
                {t("periodic-review:notice.overdue-action-contact-support")}
              </p>
            )}
            {review.deadline && (
              <p className={css.deadline}>
                {t("periodic-review:notice.overdue-deadline", {date: formatDeadline(review.deadline)})}
              </p>
            )}
          </>
        )}
        <div className={css.btn_container}>
          <Button type="button" className={css.btn__primary} onClick={onHide}>
            {isStatus(status, PERIODIC_REVIEW_STATUS.OVERDUE) && !review.is_relation_inactive
              ? t("periodic-review:notice.close-overdue")
              : t("periodic-review:notice.close")}
          </Button>
        </div>
      </div>
    );
  };

  if (!review) return null;

  // InProgress shows the form outright; Overdue shows it once the notice is acknowledged
  // (never when the relation is inactive — see onHide: that submit would 409).
  const isForm =
    isStatus(review.status, PERIODIC_REVIEW_STATUS.IN_PROGRESS) ||
    (isStatus(review.status, PERIODIC_REVIEW_STATUS.OVERDUE) && acknowledged && !review.is_relation_inactive);
  const noticeKey = isStatus(review.status, PERIODIC_REVIEW_STATUS.TIMEDOUT) ? "timedout" : "overdue";

  return (
    <Modal
      visible={visible}
      title={isForm ? t("periodic-review:title") : t(`periodic-review:notice.${noticeKey}-title`)}
      onHide={onHide}
      hideCloseButton={isForm}
      closeOnOverlayClick={!isForm}
      closeOnEsc={!isForm}
    >
      {isForm ? (
        <CustomForm
          initialValues={getInitialValues(review.current)}
          onSubmit={onSubmit}
          customValidate={validate}
          render={renderForm}
        />
      ) : (
        renderNotice(review.status)
      )}
    </Modal>
  );
};

export default withTranslation(namespacesRequired)(PeriodicReviewModal);
